import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Moon, BedDouble, Coffee, HeartPulse, Sun } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid } from "recharts";

const today = () => new Date().toISOString().slice(0, 10);
const SLEEP_QUICK = [5, 6, 7, 7.5, 8, 9];
const QUALITY = [
  { id: 1, label: "Poor",  sub: "Restless / <5h" },
  { id: 2, label: "OK",    sub: "Enough to function" },
  { id: 3, label: "Good",  sub: "Recovered" },
  { id: 4, label: "Great", sub: "Fully charged" },
];
const TIPS = [
  "Guard 7+ hours as the target — under 6h consistently is a red flag.",
  "Consistent bed and wake time beats total hours in bed.",
  "Cool, dark room. No screens for 30 min before sleep.",
  "Caffeine cut-off: 8+ hours before bed.",
  "Rest days that are actually rest — walk gently, don't crash-train.",
];

export default function Recover() {
  const [logs, setLogs] = useState([]);
  const [hours, setHours] = useState("");
  const [quality, setQuality] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/track/logs");
      setLogs(r.data);
      const t = r.data.find((l) => l.date === today());
      if (t) {
        setHours(t.sleep_hours ? String(t.sleep_hours) : "");
        setQuality(t.recovery_quality ?? null);
      }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const save = async (patch, successMsg) => {
    setSaving(true);
    try {
      await api.post("/track/log", { date: today(), ...patch });
      if (successMsg) toast.success(successMsg);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const saveHours = (val) => {
    const n = Number(val);
    if (Number.isNaN(n) || n < 0 || n > 14) return;
    setHours(String(val));
    save({ sleep_hours: n }, `${val}h logged.`);
  };

  const saveQuality = (q) => {
    setQuality(q);
    save({ recovery_quality: q }, "Recovery logged.");
  };

  const stats = useMemo(() => {
    const sevenAgo = new Date(); sevenAgo.setDate(sevenAgo.getDate() - 7);
    const recent = logs.filter((l) => new Date(l.date) >= sevenAgo);
    const withSleep = recent.filter((l) => l.sleep_hours > 0);
    const avg = withSleep.length ? withSleep.reduce((s, l) => s + Number(l.sleep_hours), 0) / withSleep.length : 0;
    const nights7plus = withSleep.filter((l) => l.sleep_hours >= 7).length;
    const withQuality = recent.filter((l) => l.recovery_quality);
    const avgQuality = withQuality.length ? withQuality.reduce((s, l) => s + l.recovery_quality, 0) / withQuality.length : 0;
    return { avg: Math.round(avg * 10) / 10, nights7plus, avgQuality: Math.round(avgQuality * 10) / 10, sampleSize: withSleep.length };
  }, [logs]);

  const chartData = logs
    .slice()
    .reverse()
    .filter((l) => l.sleep_hours > 0)
    .slice(-14)
    .map((l) => ({ date: l.date.slice(5), hours: Number(l.sleep_hours) }));

  const band = stats.avg >= 7.5 ? { l: "RECOVERED", c: "text-primary" }
    : stats.avg >= 6.5 ? { l: "ON TRACK",  c: "text-foreground" }
    : stats.avg >= 5   ? { l: "REBUILD",   c: "text-foreground" }
    : { l: "RESET", c: "text-destructive" };

  return (
    <div className="space-y-8">
      <div>
        <div className="label-eyebrow">Recover · Sleep & Recovery</div>
        <h1 className="wordmark mt-3 text-4xl md:text-5xl uppercase">Recovery Is Training.</h1>
        <p className="mt-2 text-muted-foreground max-w-xl">Guard 7+ hours a night. You do not out-train poor sleep.</p>
      </div>

      {/* Today's log */}
      <div className="bg-card border border-border rounded-lg p-6 md:p-8" data-testid="sleep-logger">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="label-eyebrow">Last Night · {today()}</div>
            <h2 className="wordmark mt-2 text-3xl uppercase">Log your sleep.</h2>
            <p className="text-sm text-muted-foreground mt-1">Enter hours slept. Then rate how recovered you feel today.</p>
          </div>
          <div className="text-right">
            <div className="label-eyebrow">Hours</div>
            <div className="wordmark text-6xl text-primary leading-none" data-testid="sleep-hours-display">
              {hours || "0"}<span className="text-foreground/40 text-3xl">h</span>
            </div>
          </div>
        </div>

        <div className="mt-6">
          <div className="label-eyebrow mb-3">Quick log</div>
          <div className="flex flex-wrap gap-2">
            {SLEEP_QUICK.map((h) => (
              <button key={h}
                onClick={() => saveHours(h)}
                disabled={saving}
                className={`px-4 py-2 rounded-md border font-head uppercase tracking-wider text-sm transition-colors ${Number(hours) === h ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-primary"}`}
                data-testid={`sleep-quick-${h}`}>
                {h}h
              </button>
            ))}
            <input
              type="number" step="0.5" min="0" max="14"
              value={hours} onChange={(e) => setHours(e.target.value)}
              onBlur={(e) => e.target.value && saveHours(e.target.value)}
              placeholder="Custom"
              className="w-28 rounded-md border border-input bg-background px-3 py-2 text-sm"
              data-testid="sleep-hours-input"
            />
          </div>
        </div>

        <div className="mt-8">
          <div className="label-eyebrow mb-3">How recovered do you feel?</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {QUALITY.map((q) => {
              const on = quality === q.id;
              const Icon = q.id === 1 ? Coffee : q.id === 2 ? BedDouble : q.id === 3 ? HeartPulse : Sun;
              return (
                <button key={q.id}
                  onClick={() => saveQuality(q.id)}
                  disabled={saving}
                  className={`text-left rounded-md p-4 border transition-colors ${on ? "bg-foreground text-background border-foreground" : "bg-background border-border hover:border-primary"}`}
                  data-testid={`recovery-${q.label.toLowerCase()}`}>
                  <Icon className={on ? "text-primary" : "text-foreground/60"} size={22} strokeWidth={2.25} />
                  <div className={`mt-3 font-head uppercase text-base ${on ? "" : "text-foreground"}`}>{q.label}</div>
                  <div className={`mt-1 text-xs ${on ? "text-background/70" : "text-muted-foreground"}`}>{q.sub}</div>
                </button>
              );
            })}
          </div>
        </div>

        <div className="mt-5 text-xs text-muted-foreground">{saving ? "Saving…" : "Auto-saved to your daily log."}</div>
      </div>

      {/* Weekly stats */}
      <div className="grid md:grid-cols-3 gap-3">
        <div className="bg-foreground text-background rounded-lg p-6" data-testid="weekly-avg-card">
          <div className="label-eyebrow !text-background/70">7-day sleep avg</div>
          <div className="wordmark text-6xl mt-2 leading-none">{stats.avg || "—"}<span className="text-3xl text-background/70">h</span></div>
          <div className={`mt-3 font-head uppercase text-sm tracking-widest ${band.c === "text-primary" ? "!text-primary" : "text-background/90"}`}>{stats.avg ? band.l : "No data yet"}</div>
        </div>
        <div className="bg-card border border-border rounded-lg p-6">
          <div className="label-eyebrow">Nights ≥ 7h</div>
          <div className="wordmark text-6xl mt-2 leading-none">{stats.nights7plus}<span className="text-3xl text-muted-foreground">/7</span></div>
          <div className="mt-3 text-xs text-muted-foreground">Target: 5+ nights per week.</div>
        </div>
        <div className="bg-card border border-border rounded-lg p-6">
          <div className="label-eyebrow">Recovery feel avg</div>
          <div className="wordmark text-6xl mt-2 leading-none">{stats.avgQuality || "—"}<span className="text-3xl text-muted-foreground">/4</span></div>
          <div className="mt-3 text-xs text-muted-foreground">1 Poor · 2 OK · 3 Good · 4 Great</div>
        </div>
      </div>

      {/* Chart */}
      {chartData.length > 1 && (
        <div className="bg-card border border-border rounded-lg p-6">
          <div className="label-eyebrow">Sleep Trend</div>
          <div className="h-56 mt-3">
            <ResponsiveContainer>
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="date" fontSize={11} stroke="hsl(var(--muted-foreground))" />
                <YAxis domain={[3, 10]} fontSize={11} stroke="hsl(var(--muted-foreground))" />
                <Tooltip />
                <Line type="monotone" dataKey="hours" stroke="hsl(var(--primary))" strokeWidth={2} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Recent nights */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="label-eyebrow">Recent Nights</div>
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-widest text-muted-foreground border-b border-border">
                <th className="py-2 pr-4">Date</th>
                <th className="py-2 pr-4">Hours</th>
                <th className="py-2 pr-4">Recovery</th>
              </tr>
            </thead>
            <tbody>
              {logs.filter((l) => l.sleep_hours || l.recovery_quality).slice(0, 14).map((l) => (
                <tr key={l.date} className="border-b border-border/60">
                  <td className="py-2 pr-4">{l.date}</td>
                  <td className="py-2 pr-4">{l.sleep_hours ? `${l.sleep_hours}h` : "—"}</td>
                  <td className="py-2 pr-4">{QUALITY.find((q) => q.id === l.recovery_quality)?.label || "—"}</td>
                </tr>
              ))}
              {logs.filter((l) => l.sleep_hours || l.recovery_quality).length === 0 && (
                <tr><td colSpan="3" className="py-4 text-muted-foreground">No sleep logs yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Tips */}
      <div className="bg-foreground text-background rounded-lg p-6">
        <div className="label-eyebrow !text-background/70">Recovery Principles</div>
        <ul className="mt-4 space-y-3 text-sm">
          {TIPS.map((t, i) => <li key={i} className="flex gap-3"><span className="text-primary">→</span>{t}</li>)}
        </ul>
      </div>
    </div>
  );
}
